import React, { useState, useEffect, useCallback, useMemo } from 'react';
import SafetyDocumentForm from './SafetyDocumentForm';
import MRTTableAdapter from './MRTTableAdapter';
import { createMRTColumnHelper } from 'material-react-table';
import { API_BASE } from '../utils/api';

interface Product {
  id: number;
  name: string;
  facility_id: number;
}

interface SafetyDocument {
  id: number;
  product_id: number;
  file_path: string;
  tags?: string | string[];
  document_type?: string;
  description?: string | null;
  filename?: string;
  uploaded_by: number;
  version: number;
  created_at: string;
  status?: string;
  reviewed_at?: string;
  reviewed_by?: number;
  review_notes?: string | null;
  product?: Product;
  uploaded_at?: string;
  expiry_date?: string;
}

const SafetyDocumentDashboard: React.FC = () => {
  const [documents, setDocuments] = useState<SafetyDocument[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  // Simplified view: always show table, no filters per user request

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      const response = await fetch(`${API_BASE}/safety-documents/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setDocuments(data);
    } catch (error) {
      console.error('Error fetching safety documents:', error);
      setError('Failed to fetch safety documents. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      const response = await fetch(`${API_BASE}/products/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
      setError('Failed to fetch products. Please try again.');
    }
  }, []);

  // Compute display versions per (product_id, document_type) based on created_at ascending
  const versionMap = useMemo(() => {
    const byGroup: Record<string, SafetyDocument[]> = {};
    for (const d of documents) {
      const key = `${d.product_id}|${d.document_type || ''}`;
      if (!byGroup[key]) byGroup[key] = [];
      byGroup[key].push(d);
    }
    const map: Record<number, number> = {};
    Object.values(byGroup).forEach(group => {
      group.sort((a, b) => new Date(a.created_at || '').getTime() - new Date(b.created_at || '').getTime());
      group.forEach((doc, idx) => {
        map[doc.id] = idx + 1;
      });
    });
    return map;
  }, [documents]);

  useEffect(() => {
    fetchDocuments();
    fetchProducts();
  }, [fetchDocuments, fetchProducts]);

  const handleSubmit = async (docData: Omit<SafetyDocument, 'id' | 'file_path' | 'uploaded_by' | 'version' | 'created_at'>, file: File) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('product_id', docData.product_id.toString());
      if (Array.isArray(docData.tags) && docData.tags.length > 0) {
        formData.append('tags', docData.tags.join(','));
      }
      if (docData.document_type) formData.append('document_type', String(docData.document_type));
      if (docData.description) formData.append('description', String(docData.description));

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      const response = await fetch(`${API_BASE}/safety-documents/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to upload document');
      }

      await fetchDocuments();
      setShowForm(false);
      setSuccess('Document uploaded successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error uploading safety document:', error);
      setError(error instanceof Error ? error.message : 'Failed to upload document');
    }
  };

  // Simplified view: edit/view modals deferred; using table actions instead


  // export handlers removed

  // export handlers removed

  const getProductName = (productId: number) => {
    const product = products.find(p => p.id === productId);
    return product ? product.name : 'Unknown Product';
  };

  // const formatDate = (dateString: string) => {
  //   return new Date(dateString).toLocaleDateString();
  // };

  // const getFileName = (filePath: string) => {
  //   return filePath.split('/').pop() || filePath;
  // };

  // const getFileType = (filePath: string) => {
  //   const extension = filePath.split('.').pop()?.toLowerCase();
  //   switch (extension) {
  //     case 'pdf':
  //       return 'PDF';
  //     case 'doc':
  //     case 'docx':
  //       return 'Word';
  //     case 'txt':
  //       return 'Text';
  //     case 'jpg':
  //     case 'jpeg':
  //     case 'png':
  //       return 'Image';
  //     default:
  //       return 'Document';
  //   }
  // };

  const getTags = (tags?: string | string[]) => {
    if (!tags) return [] as string[];
    if (Array.isArray(tags)) return tags;
    return tags.split(',').map(t => t.trim()).filter(Boolean);
  };

  // label generation removed in simplified UI

  const handleApproveLabel = async (docId: number) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const res = await fetch(`${API_BASE}/safety-documents/${docId}/approve`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Approve failed');
      await fetchDocuments();
      setSuccess('Document approved successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error(err);
      setError('Failed to approve document');
    }
  };

  const handleRejectLabel = async (docId: number) => {
    try {
      const reason = prompt('Enter rejection reason (optional)') || '';
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const formData = new FormData();
      if (reason) formData.append('rejection_reason', reason);
      const res = await fetch(`${API_BASE}/safety-documents/${docId}/reject`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (!res.ok) throw new Error('Reject failed');
      await fetchDocuments();
      setSuccess('Document rejected successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error(err);
      setError('Failed to reject document');
    }
  };

  const handleViewDocument = async (docId: number, filename?: string) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const res = await fetch(`${API_BASE}/safety-documents/${docId}/download`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Download failed');
      
      // Create blob and download
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || `document_${docId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setSuccess('Document downloaded successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error(err);
      setError('Failed to download document');
    }
  };

  const handleDeleteDocument = async (docId: number) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const res = await fetch(`${API_BASE}/safety-documents/${docId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Delete failed');
      await fetchDocuments();
      setSuccess('Document deleted successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error(err);
      setError('Failed to delete document');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-200 border-t-indigo-600 mx-auto"></div>
          <h2 className="text-xl font-semibold text-gray-800 mt-6">Loading Safety Documents...</h2>
          <p className="text-gray-500 mt-2">Please wait while we fetch your safety document data</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
            <div className="flex items-center space-x-4">
              <div className="bg-blue-100 p-3 rounded-lg">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            <div>
                <h1 className="text-3xl font-bold text-gray-900">Safety Document Management</h1>
                <p className="text-gray-600 mt-1">Manage safety documents, assessments, and compliance documentation</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="bg-gray-100 px-4 py-2 rounded-lg">
                <span className="text-sm font-medium text-gray-700">Total Documents: </span>
                <span className="text-lg font-bold text-blue-600">{documents.length}</span>
              </div>
              <button
                onClick={() => setShowForm(true)}
                className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 flex items-center shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Upload Document
              </button>
            </div>
          </div>
                </div>

        {/* Success Alert */}
        {success && (
          <div className="bg-green-50 border-l-4 border-green-400 rounded-lg p-4 mb-6 animate-in slide-in-from-top-2 duration-300">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-green-800">Success</h3>
                <div className="mt-2 text-sm text-green-700">
                  <p>{success}</p>
                </div>
                <div className="mt-4">
                  <button
                    onClick={() => setSuccess(null)}
                    className="text-sm font-medium text-green-800 hover:text-green-900 underline"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border-l-4 border-red-400 rounded-lg p-4 mb-6 animate-in slide-in-from-top-2 duration-300">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
            </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{error}</p>
          </div>
                <div className="mt-4">
            <button
                    onClick={() => setError(null)}
                    className="text-sm font-medium text-red-800 hover:text-red-900 underline"
                  >
                    Dismiss
            </button>
          </div>
        </div>
            </div>
          </div>
        )}

        {/* Explanation Board (Improved) */}
        <div className="mb-8 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">📋 Safety Document Management</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">End-to-End Flow</h3>
              <div className="flex items-center space-x-3">
                <div className="flex flex-col items-center">
                  <div className="w-9 h-9 bg-blue-700 text-white rounded-full flex items-center justify-center text-xs font-bold">1</div>
                  <span className="text-xs mt-1 text-gray-900 text-center">Upload</span>
                </div>
                <div className="flex-1 h-0.5 bg-blue-400"></div>
                <div className="flex flex-col items-center">
                  <div className="w-9 h-9 bg-amber-600 text-white rounded-full flex items-center justify-center text-xs font-bold">2</div>
                  <span className="text-xs mt-1 text-gray-900 text-center">Review</span>
                </div>
                <div className="flex-1 h-0.5 bg-blue-400"></div>
                <div className="flex flex-col items-center">
                  <div className="w-9 h-9 bg-green-700 text-white rounded-full flex items-center justify-center text-xs font-bold">3</div>
                  <span className="text-xs mt-1 text-gray-900 text-center">Approve/Reject</span>
                </div>
                <div className="flex-1 h-0.5 bg-blue-400"></div>
                <div className="flex flex-col items-center">
                  <div className="w-9 h-9 bg-purple-700 text-white rounded-full flex items-center justify-center text-xs font-bold">4</div>
                  <span className="text-xs mt-1 text-gray-900 text-center">Use in Product</span>
                </div>
              </div>
              <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200 text-sm text-gray-900">
                • Labels are documents of type "label"; approval sets status=approved.<br/>
                • Approved labels indicate market readiness and appear in product labels.
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Document Types</h3>
              <div className="grid grid-cols-2 gap-2 text-sm text-gray-900">
                <div>• Safety Assessment</div>
                <div>• Ingredient Safety</div>
                <div>• Product Safety</div>
                <div>• Clinical Data</div>
                <div>• Label</div>
                <div>• Other</div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters removed per user request */}

        {showForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/50" onClick={() => setShowForm(false)}></div>
            <div className="relative bg-white rounded-xl shadow-xl w-full max-w-xl mx-4 p-4 sm:p-6 animate-in slide-in-from-top-2 max-h-[85vh] overflow-auto">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Upload New Safety Document</h2>
                <button onClick={() => setShowForm(false)} className="text-gray-500 hover:text-gray-700">✕</button>
              </div>
              <SafetyDocumentForm onSubmit={handleSubmit} initialData={undefined} />
            </div>
          </div>
        )}

        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          {documents.length === 0 ? (
            <div className="px-6 py-4 text-center text-gray-500">
              No safety documents uploaded yet.
            </div>
          ) : (
            (() => {
              const columnHelper = createMRTColumnHelper<SafetyDocument>();
              const columns: any[] = [
                columnHelper.accessor('id', { header: 'ID', size: 60 }),
                columnHelper.accessor(row => row.file_path?.split('/').pop() || '', { id: 'filename', header: 'Filename', size: 220 }),
                columnHelper.accessor('document_type', { header: 'Type', size: 130, filterVariant: 'select' }),
                columnHelper.accessor('product_id', { header: 'Product', size: 160, Cell: ({ row }: any) => getProductName(row.original.product_id) }),
                columnHelper.display({
                  id: 'version',
                  header: 'Version',
                  size: 100,
                  Cell: ({ row }: any) => {
                    const doc = row.original as SafetyDocument;
                    const computed = versionMap[doc.id];
                    const display = computed ? `v${computed}` : '-';
                    return (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-900">
                        {display}
                      </span>
                    );
                  },
                }),
                columnHelper.accessor(row => (row.file_path?.split('.').pop() || '').toLowerCase(), { id: 'file_type', header: 'File Type', size: 110, filterVariant: 'select' }),
                columnHelper.accessor(row => (Array.isArray(row.tags) ? row.tags.join(', ') : (row.tags || '')), { id: 'tags', header: 'Tags', size: 180 }),
                columnHelper.accessor('description', { header: 'Description', size: 220 }),
                columnHelper.accessor(row => (row.status || 'uploaded'), { id: 'status', header: 'Status', size: 110, filterVariant: 'select' }),
                columnHelper.accessor(row => (row.created_at ? new Date(row.created_at).toISOString().slice(0,10) : ''), { id: 'created_at', header: 'Created', size: 130, filterVariant: 'date-range' }),
                columnHelper.display({
                  id: 'actions',
                  header: 'Actions',
                  Cell: ({ row }: any) => {
                    const doc = row.original as SafetyDocument;
                    const isLabel = (doc.document_type === 'label') || getTags(doc.tags).includes('label');
                    return (
                      <div className="flex items-center gap-3">
                        <button onClick={() => handleViewDocument(doc.id, doc.filename)} className="text-indigo-600 hover:text-indigo-900">View</button>
                        {isLabel && (
                          <>
                            <button onClick={() => handleApproveLabel(doc.id)} className="text-emerald-600 hover:text-emerald-900">Approve</button>
                            <button onClick={() => handleRejectLabel(doc.id)} className="text-yellow-700 hover:text-yellow-900">Reject</button>
                          </>
                        )}
                        <button onClick={() => handleDeleteDocument(doc.id)} className="text-red-600 hover:text-red-900">Delete</button>
                      </div>
                    );
                  },
                  size: 240,
                }),
              ];
              return <MRTTableAdapter<SafetyDocument> columns={columns} data={documents} />;
            })()
          )}
        </div>
      </div>
    </div>
  );
};

export default SafetyDocumentDashboard; 